/* Suivi du job d'import groupé de copies (NAI-222, plan v7) — polling local
 * à l'écran qui l'affiche, pas le contexte global `useExamImportJobs` (spec
 * 13) : ce dernier est keyé par documentId avec des champs propres à la
 * structuration (sectionsTotal/sectionsCreated) et n'expose pas d'endpoint
 * "jobs actifs" pour ce jobKind — le généraliser serait un chantier séparé,
 * pas un besoin de cet écran (qui reste ouvert pendant tout l'import, sans
 * navigation à survivre). */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export interface BulkImportJob {
  readonly id: string;
  readonly status: 'running' | 'succeeded' | 'failed';
  readonly step: 'generating' | 'saving' | 'done';
  readonly pagesTotal: number;
  readonly copiesPersisted: number;
  readonly error: string | null;
  readonly failureKind: string | null;
}

const POLL_MS = 2_000;

export function useBulkImportJob(sessionId: string | null, active: boolean) {
  const [job, setJob] = useState<BulkImportJob | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const poll = useCallback(async () => {
    if (!sessionId) return;
    const row = await api.exam.bulkImportJob.query({ sessionId }).catch(() => null);
    if (!row) return;
    setJob({
      id: row.id,
      status: row.status,
      step: row.step,
      pagesTotal: row.pages_total,
      copiesPersisted: row.copies_persisted,
      error: row.error,
      failureKind: row.failure_kind,
    });
  }, [sessionId]);

  useEffect(() => {
    if (!active || !sessionId) return;
    void poll();
    const timer = window.setInterval(() => { if (activeRef.current) void poll(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [active, sessionId, poll]);

  return { job, refresh: poll };
}
